import { useEffect, useMemo, useState } from 'react'
import { skillModules } from '../data/skillModules'
import { moduleIndustries } from '../data/moduleIndustries'
import {
  AudienceLevel,
  Industry,
  INDUSTRY_LABELS,
  LEVEL_LABELS,
} from '../types/pathway'
import {
  matchModules,
  ModuleSort,
  parseTopics,
  sortModules,
} from '../utils/pathwayMatching'

export const LIBRARY_PAGE_SIZE = 8

export interface ActiveFilter {
  id: string
  label: string
  onRemove: () => void
}

/** Every topic used across the skill module library, alphabetized. */
export const LIBRARY_TOPICS: string[] = Array.from(
  new Set(skillModules.flatMap((m) => m.keywords)),
).sort((a, b) => a.localeCompare(b))

export const topicLabel = (topic: string) =>
  topic.charAt(0).toUpperCase() + topic.slice(1)

export function useModuleLibrary() {
  const [industry, setIndustry] = useState<Industry | 'all'>('all')
  const [role, setRole] = useState<AudienceLevel | 'all'>('all')
  const [topic, setTopic] = useState<string>('all')
  const [keywords, setKeywords] = useState('')
  const [keywordTerms, setKeywordTerms] = useState<string[]>([])
  const [sort, setSort] = useState<ModuleSort>('match')
  const [page, setPage] = useState(1)

  const typedTerms = useMemo(() => parseTopics(keywords), [keywords])
  const keywordKey = typedTerms.join('|')

  // Debounced so the list doesn't reshuffle on every keystroke.
  useEffect(() => {
    const timer = window.setTimeout(
      () => setKeywordTerms(keywordKey ? keywordKey.split('|') : []),
      300,
    )
    return () => window.clearTimeout(timer)
  }, [keywordKey])

  useEffect(() => {
    setPage(1)
  }, [industry, role, topic, keywordKey, sort])

  const results = useMemo(
    () =>
      sortModules(
        matchModules(keywordTerms, role === 'all' ? null : role).filter(
          ({ module }) =>
            (industry === 'all' ||
              (moduleIndustries[module.id] ?? []).includes(industry)) &&
            (role === 'all' || module.levels.includes(role)) &&
            (topic === 'all' || module.keywords.includes(topic)),
        ),
        sort,
      ),
    [keywordTerms, industry, role, topic, sort],
  )

  const clearAll = () => {
    setIndustry('all')
    setRole('all')
    setTopic('all')
    setKeywords('')
    setKeywordTerms([])
    setSort('match')
    setPage(1)
  }

  const activeFilters: ActiveFilter[] = [
    ...(industry !== 'all'
      ? [
          {
            id: 'industry',
            label: INDUSTRY_LABELS[industry],
            onRemove: () => setIndustry('all'),
          },
        ]
      : []),
    ...(role !== 'all'
      ? [{ id: 'role', label: LEVEL_LABELS[role], onRemove: () => setRole('all') }]
      : []),
    ...(topic !== 'all'
      ? [{ id: 'topic', label: topicLabel(topic), onRemove: () => setTopic('all') }]
      : []),
    ...typedTerms.map((term) => ({
      id: `keyword-${term}`,
      label: `“${term}”`,
      onRemove: () =>
        setKeywords(typedTerms.filter((t) => t !== term).join(', ')),
    })),
  ]

  const pageCount = Math.max(1, Math.ceil(results.length / LIBRARY_PAGE_SIZE))
  const safePage = Math.min(page, pageCount)
  const pageItems = results.slice(
    (safePage - 1) * LIBRARY_PAGE_SIZE,
    safePage * LIBRARY_PAGE_SIZE,
  )

  return {
    industry,
    role,
    topic,
    keywords,
    setIndustry,
    setRole,
    setTopic,
    setKeywords,
    sort,
    setSort,
    results,
    pageItems,
    page: safePage,
    pageCount,
    setPage,
    activeFilters,
    hasFilters: activeFilters.length > 0,
    isRanked: keywordTerms.length > 0,
    clearAll,
  }
}

export type ModuleLibrary = ReturnType<typeof useModuleLibrary>
